/**
 * Single source of truth for services menu configuration
 */

import type { SwissLanguage } from '@/utils/languageTypes';

// 17 services (current stack)
export type ServiceId =
  | "ai-video"
  | "webdesign"
  | "seo"
  | "gbp" // Google Business Profile & Reviews
  | "video-editing"
  | "ai-influencer"
  | "paid-ads"
  | "3d-modeling"
  | "motion-graphics"
  | "podcast"
  | "ai-copywriting"
  | "automation"
  | "email-marketing"
  | "ux-ui"
  | "ecommerce"
  | "ar-vr"
  | "ai-music";

export type ServiceGroup = "Creative" | "Growth" | "Automation" | "Prestige";

export type ServiceMenuItem = {
  id: ServiceId;
  group: ServiceGroup;
  // base hub path factory per locale, e.g. /de/seo/
  path: (locale: SwissLanguage) => string;
  // visible label per locale
  label: Record<SwissLanguage, string>;
  // optional sub-services for fly-out/mega menu
  children?: Array<{
    id: string;
    label: Record<SwissLanguage, string>;
    path: (locale: SwissLanguage) => string;
  }>;
  // sort weight within group (lower = higher)
  priority?: number;
  // add city quick links in the flyout
  cityQuickLinks?: boolean;
  // icon from lucide-react
  icon?: string;
  // badges for promotion
  badge?: 'NEW' | 'POPULAR';
};

export const SERVICES_MENU: ServiceMenuItem[] = [
  // CREATIVE
  {
    id: "ai-video",
    group: "Creative",
    path: l => `/${l}/ai-video/`,
    label: { 
      de: "AI-Video & Performance Video", 
      fr: "Vidéo IA & performance", 
      it: "Video IA & performance", 
      en: "AI Video & Performance Video" 
    },
    priority: 5,
    icon: "video",
    cityQuickLinks: true,
  },
  {
    id: "video-editing",
    group: "Creative",
    path: l => `/${l}/video-editing/`,
    label: { 
      de: "Video Editing", 
      fr: "Montage vidéo", 
      it: "Video editing", 
      en: "Video Editing" 
    },
    priority: 10,
    icon: "film",
  },
  {
    id: "ai-influencer",
    group: "Creative",
    path: l => `/${l}/ai-influencer/`,
    label: { 
      de: "AI-Influencer", 
      fr: "Influenceur IA", 
      it: "Influencer IA", 
      en: "AI Influencer" 
    },
    priority: 20,
    icon: "bot",
    badge: "NEW",
  },
  {
    id: "3d-modeling",
    group: "Creative",
    path: l => `/${l}/3d-modeling/`,
    label: { 
      de: "3D Modeling & Animation", 
      fr: "Modélisation 3D & Animation", 
      it: "Modellazione 3D & Animazione", 
      en: "3D Modeling & Animation" 
    },
    priority: 30,
    icon: "cube",
  },
  {
    id: "motion-graphics",
    group: "Creative",
    path: l => `/${l}/motion-graphics/`,
    label: { 
      de: "Motion Graphics", 
      fr: "Motion design", 
      it: "Motion graphics", 
      en: "Motion Graphics" 
    },
    priority: 40,
    icon: "wand2",
  },
  {
    id: "podcast",
    group: "Creative",
    path: l => `/${l}/podcast-editing/`,
    label: { 
      de: "Podcast Editing & Launch", 
      fr: "Podcast: montage & lancement", 
      it: "Podcast: editing & lancio", 
      en: "Podcast Editing & Launch" 
    },
    priority: 50,
    icon: "headphones",
  },

  // GROWTH
  {
    id: "webdesign",
    group: "Growth",
    path: l => `/${l}/webdesign/`,
    label: { 
      de: "Webdesign & Redesign", 
      fr: "Webdesign & refonte", 
      it: "Web design & redesign", 
      en: "Web Design & Redesign" 
    },
    priority: 10,
    cityQuickLinks: true,
    icon: "monitor",
  },
  {
    id: "seo",
    group: "Growth",
    path: l => `/${l}/seo/`,
    label: { 
      de: "SEO (inkl. Multilingual)", 
      fr: "SEO (dont multilingue)", 
      it: "SEO (anche multilingue)", 
      en: "SEO (incl. Multilingual)" 
    },
    priority: 20,
    cityQuickLinks: true,
    icon: "search",
    children: [
      { 
        id: "local", 
        label: { de: "Local SEO", fr: "SEO local", it: "SEO locale", en: "Local SEO" }, 
        path: l => `/${l}/seo/local/` 
      },
      { 
        id: "ecom", 
        label: { de: "E-Commerce SEO", fr: "SEO e-commerce", it: "SEO e-commerce", en: "E-commerce SEO" }, 
        path: l => `/${l}/seo/ecommerce/` 
      },
      { 
        id: "intl", 
        label: { de: "International SEO", fr: "SEO international", it: "SEO internazionale", en: "International SEO" }, 
        path: l => `/${l}/seo/international/` 
      },
      { 
        id: "technical", 
        label: { de: "Technisches SEO", fr: "SEO technique", it: "SEO tecnico", en: "Technical SEO" }, 
        path: l => `/${l}/seo/technical/` 
      },
    ],
  },
  {
    id: "paid-ads",
    group: "Growth",
    path: l => `/${l}/paid-ads/`,
    label: { 
      de: "Paid Ads (Google/Meta/LinkedIn/TikTok)", 
      fr: "Publicités payantes", 
      it: "Advertising a pagamento", 
      en: "Paid Ads (Google/Meta/LinkedIn/TikTok)" 
    },
    priority: 30,
    icon: "megaphone",
    badge: "POPULAR",
  },
  {
    id: "gbp",
    group: "Growth",
    path: l => `/${l}/google-business-profile/`,
    label: { 
      de: "Google Unternehmensprofil & Reviews", 
      fr: "Profil d'entreprise Google & avis", 
      it: "Profilo Aziendale Google & recensioni", 
      en: "Google Business Profile & Reviews" 
    },
    priority: 40,
    icon: "map-pin",
  },

  // AUTOMATION
  {
    id: "ai-copywriting",
    group: "Automation",
    path: l => `/${l}/ai-copywriting/`,
    label: { 
      de: "AI Copywriting", 
      fr: "Rédaction IA", 
      it: "Copywriting IA", 
      en: "AI Copywriting" 
    },
    priority: 10,
    icon: "pen-tool",
  },
  {
    id: "automation",
    group: "Automation",
    path: l => `/${l}/automation/`,
    label: { 
      de: "Business Process Automation", 
      fr: "Automatisation des processus", 
      it: "Automazione dei processi", 
      en: "Business Process Automation" 
    },
    priority: 20,
    icon: "cpu",
  },
  {
    id: "email-marketing",
    group: "Automation",
    path: l => `/${l}/email-marketing/`,
    label: { 
      de: "E-Mail Marketing Automation", 
      fr: "Automatisation e-mail", 
      it: "Automazione e-mail", 
      en: "Email Marketing Automation" 
    },
    priority: 30,
    icon: "mail",
  },

  // PRESTIGE
  {
    id: "ux-ui",
    group: "Prestige",
    path: l => `/${l}/ux-ui-design/`,
    label: { 
      de: "UX/UI Design", 
      fr: "UX/UI Design", 
      it: "UX/UI Design", 
      en: "UX/UI Design" 
    },
    priority: 10,
    icon: "palette",
  },
  {
    id: "ecommerce",
    group: "Prestige",
    path: l => `/${l}/ecommerce/`,
    label: { 
      de: "E-Commerce Customization", 
      fr: "Personnalisation e-commerce", 
      it: "Personalizzazione e-commerce", 
      en: "E-Commerce Customization" 
    },
    priority: 20,
    icon: "shopping-cart",
  },
  {
    id: "ar-vr",
    group: "Prestige",
    path: l => `/${l}/ar-vr/`,
    label: { 
      de: "AR/VR Experiences", 
      fr: "Expériences AR/VR", 
      it: "Esperienze AR/VR", 
      en: "AR/VR Experiences" 
    },
    priority: 30,
    icon: "vr",
    badge: "NEW",
  },
  {
    id: "ai-music",
    group: "Prestige",
    path: l => `/${l}/ai-music/`,
    label: { 
      de: "AI Music & Sound Branding", 
      fr: "Identité sonore IA", 
      it: "AI Music & Sound Branding", 
      en: "AI Music & Sound Branding" 
    },
    priority: 40,
    icon: "music2",
  },
];

/**
 * Get services by group, sorted by priority
 */
export const getServicesByGroup = (group: ServiceGroup): ServiceMenuItem[] => {
  return SERVICES_MENU
    .filter(service => service.group === group)
    .sort((a, b) => (a.priority || 999) - (b.priority || 999));
};

/**
 * Get all groups with their services
 */
export const getServicesGrouped = () => {
  const groups: ServiceGroup[] = ["Creative", "Growth", "Automation", "Prestige"];
  return groups.map(group => ({
    name: group,
    services: getServicesByGroup(group)
  }));
};

/**
 * Get top services by priority (for footer)
 */
export const getTopServices = (limit: number = 8): ServiceMenuItem[] => {
  return [...SERVICES_MENU]
    .sort((a, b) => (a.priority || 999) - (b.priority || 999))
    .slice(0, limit);
};

/**
 * Find service by ID
 */
export const getServiceById = (id: ServiceId): ServiceMenuItem | undefined => {
  return SERVICES_MENU.find(service => service.id === id);
};

/**
 * Group labels per locale
 */
export const GROUP_LABELS: Record<ServiceGroup, Record<SwissLanguage, string>> = {
  Creative: {
    de: "Kreative Services",
    en: "Creative Services", 
    fr: "Services Créatifs",
    it: "Servizi Creativi"
  },
  Growth: {
    de: "Wachstum & Marketing",
    en: "Growth & Marketing",
    fr: "Croissance & Marketing", 
    it: "Crescita & Marketing"
  },
  Automation: {
    de: "Automatisierung",
    en: "Automation",
    fr: "Automatisation",
    it: "Automazione"
  },
  Prestige: {
    de: "Premium Services",
    en: "Prestige Services",
    fr: "Services Premium",
    it: "Servizi Premium"
  }
};

/**
 * Swiss cities for quick links
 */
export const SWISS_CITIES = [
  { id: 'zurich', name: { de: 'Zürich', en: 'Zurich', fr: 'Zurich', it: 'Zurigo' }, slug: 'zurich' },
  { id: 'basel', name: { de: 'Basel', en: 'Basel', fr: 'Bâle', it: 'Basilea' }, slug: 'basel' },
  { id: 'bern', name: { de: 'Bern', en: 'Bern', fr: 'Berne', it: 'Berna' }, slug: 'bern' },
  { id: 'geneva', name: { de: 'Genf', en: 'Geneva', fr: 'Genève', it: 'Ginevra' }, slug: 'geneva' },
  { id: 'lausanne', name: { de: 'Lausanne', en: 'Lausanne', fr: 'Lausanne', it: 'Losanna' }, slug: 'lausanne' },
  { id: 'lugano', name: { de: 'Lugano', en: 'Lugano', fr: 'Lugano', it: 'Lugano' }, slug: 'lugano' },
  { id: 'winterthur', name: { de: 'Winterthur', en: 'Winterthur', fr: 'Winterthour', it: 'Winterthur' }, slug: 'winterthur' },
  { id: 'stgallen', name: { de: 'St. Gallen', en: 'St. Gallen', fr: 'Saint-Gall', it: 'San Gallo' }, slug: 'st-gallen' }
] as const;